<template>
	<div>
		<h1 class="mb-4">{{ title }}</h1>
		<v-filters
			class="mb-4"
			v-bind="{
				filters: urlFilters,
				tariffNames: TARIFF_NAMES,
				loading
			}"
			@update-filters="updateUrl"
		/>
		<v-data-table
			class="limit-statistic-table"
			:headers="flatHeadersForData"
			:items="tableItems"
			:loading="loading.table"
			:options.sync="options"
			:server-items-length="limitStatisticTotal"
			:items-per-page="limit"
			:footer-props="{
			itemsPerPageOptions: [20, 50, 100],
			itemsPerPageText: 'Строк на стр.'
			}"
			disable-pagination
			hide-default-header
			@update:sort-by="updateSortBy"
			@update:sort-desc="updateSortDesc"
			fixed-header
			height="calc(100vh - 225px)"
			:item-class="getItemClass"
		>
			<template v-slot:header>
				<thead>
					<tr>
						<th
							v-for="(header, index) in flatHeaders"
							:key="`header-${index}`"
							class="sortable-header"
							@click="onSort(header)"
							:class="[
								'text-center',
								'custom-th',
								{ 'v-data-table__divider': header.divider },
								{ 'sortable-header-cell': isHeaderSortable(header) }
							]"
							:colspan="header.colspan || 1"
							:rowspan="header.children ? 1 : 2"
						>
							<v-row class="align-center flex-nowrap ma-0 pa-0">
								<span>{{ header.text }}</span>

								<v-icon
									v-if="isHeaderSortable(header) && isSortedBy(header)"
									small
									class="ml-1"
								>
									{{ isSortDesc(header)
									? 'mdi-arrow-down'
									: 'mdi-arrow-up'
									}}
								</v-icon>
							</v-row>
						</th>
					</tr>
					<tr v-if="hasNestedHeaders">
						<template v-for="(header, index) in headers">
							<template v-if="header.children">
								<th
									v-for="(child, childIndex) in header.children"
									:key="`subheader-${index}-${childIndex}`"
									:class="[
										'text-center',
										{ 'v-data-table__divider': child.divider }
									]"
								>
									{{ child.text }}
								</th>
							</template>
						</template>
					</tr>
					<tr>
						<template v-for="(header, index) in headers">
							<template v-if="header.children">
								<th
									v-for="(child, childIndex) in header.children"
									:key="`filter-${index}-${childIndex}`"
									:class="[
										'text-center',
										'search-cell',
										{ 'v-data-table__divider': child.divider }
									]"
								>

									<v-select
										v-if="child.filterType === 'select'"
										:items="statusFilterItems"
										:value="getSelectFilterValue(child.searchKey)"
										dense
										outlined
										hide-details
										item-text="text"
										item-value="value"
										placeholder="—"
										@change="onSelectFilterChange(child.searchKey, $event)"
									/>

									<v-text-field
										v-if="child.filterType === 'search'"
										v-model="columnFilters[child.value]"
										:value="$route.query[child.searchKey]"
										@input="onFilterChange(child.searchKey, $event)"
										placeholder="поиск"
										class="text-caption mx-auto"
										dense
										outlined
										hide-details
										height="28"
										:style="{ minWidth: '100px' }"
									/>
								</th>
							</template>
							<th
								v-else
								:key="`filter-${index}`"
								:class="[
									'text-center',
									{ 'v-data-table__divider': header.divider }
								]"
							></th>
						</template>
					</tr>
				</thead>
			</template>
			<template v-slot:item.ywm_status="{ item }">
				<div :class="['status-cell', getStatus(item.integration.ywm, 'ywm').color]">
					{{ getStatus(item.integration.ywm, 'ywm').text }}
				</div>
			</template>
			<template v-slot:item.gsc_status="{ item }">
				<div :class="['status-cell', getStatus(item.integration.gsc, 'gsc').color]">
					{{ getStatus(item.integration.gsc, 'gsc').text }}
				</div>
			</template>
			<template v-slot:item.ga4_status="{ item }">
				<div :class="['status-cell', getStatus(item.integration.ga4, 'ga4').color]">
					{{ getStatus(item.integration.ga4, 'ga4').text }}
				</div>
			</template>
			<template v-slot:item.counter="{ item }">
				<div v-if="getActiveCounters(item).length">
					<div v-for="(counter, i) in getActiveCounters(item)" :key="i">
						{{ counter.counter }}
					</div>
				</div>
				<div v-else>-</div>
			</template>
			<template v-slot:item.yla_status="{ item }">
				<div :class="['status-cell', getStatus(item.integration.yla, 'yla').color]">
					{{ getStatus(item.integration.yla, 'yla').text }}
				</div>
			</template>
			<template v-slot:item.ym_status="{ item }">
				<div :class="['status-cell', getStatus(item.integration.ym, 'ym').color]">
					{{ getStatus(item.integration.ym, 'ym').text }}
				</div>
			</template>
			<template v-slot:item.active_type="{ item }">
				<v-switch
					v-if="item.integration.ym.is_ym_integration_active || item.integration.yla.is_yla_integration_active"
					:input-value="item.active_type === 'yla'"
					inset
					@change="switchTypeYMIntegration(item)"
				>
					<template #label>
						{{ item.active_type === 'ym' ? 'YandexMetrica' : 'LogsApi' }}
					</template>
				</v-switch>
				<span v-else>
					-
				</span>
			</template>
			<template v-slot:item.number="{ index }">
				<div>{{ index + 1 }}</div>
			</template>
			<template v-slot:item.statistics.current_semantic_count="{ item }">
				<div>{{ item.statistics.current_semantic_count.toLocaleString() }}</div>
			</template>
			<template v-slot:item.statistics.total_finished_semantic_count="{ item }">
				<div>{{ item.statistics.total_finished_semantic_count.toLocaleString() }}</div>
			</template>
			<template v-slot:item.statistics.update_limits_prc="{ item }">
				<div>{{ Math.round(item.statistics.update_limits_prc) }} %</div>
			</template>
		</v-data-table>
	</div>
</template>

<script>
import { validateFilterFromOptions, validateFilterNumber } from '@/function/validateFilter'
import updateUrlMixin from '@/mixins/updateUrlMixin'
import filters from './filters.vue'
import errors from '@/store/errors'
import { mapActions, mapState } from 'vuex'
import { NOTIFY_STATUS } from '@/const'

const TARIFF_NAMES = ['Base', 'Pro', 'Premium']
const SORTING_AVAILABLE = [
	'client_id',
	'project_id',
	'client_name',
	'project_name',
	'tariff_name',
	'main_project_host',
	'current_semantic_count',
	'previous_month_updates_count',
	'total_finished_semantic_count',
	'update_limits_prc',
	'ywm_active_hosts',
	'gsc_active_hosts',
	'ym_counter_name',
	'ga_view_name'
]

const STATUS_FILTER_ITEMS = [
	{ text: '—', value: null },
	{ text: 'Активна', value: 'active' },
	{ text: 'Неактивна', value: 'inactive' },
	{ text: 'Невалидный токен', value: 'invalid_token' },
	{ text: 'Невалидный хост/счетчик/профиль', value: 'invalid_entity' }

]

const BLOCK_CONFIG = {
	ywm: {
		label: 'хост',
		arrayKey: 'ywm_hosts',
		activeKey: 'is_ywm_integration_active',
		tokenKey: 'ywm_integration_token_valid',
		deletedKey: 'is_host_deleted',
		verifiedKey: 'is_host_verified'
	},
	ym: {
		label: 'счетчик',
		idKey: 'ym_id',
		arrayKey: 'ym_counter',
		activeKey: 'is_ym_integration_active',
		tokenKey: 'ym_integration_token_valid',
		deletedKey: 'is_counter_deleted'
	},
	yla: {
		label: 'счетчик',
		idKey: 'yla_id',
		arrayKey: 'yla_counter',
		activeKey: 'is_yla_integration_active',
		tokenKey: 'yla_integration_token_valid',
		deletedKey: 'is_counter_deleted'
	},
	gsc: {
		label: 'хост',
		arrayKey: 'gsc_hosts',
		activeKey: 'is_gsc_integration_active',
		tokenKey: 'gsc_integration_token_valid',
		deletedKey: 'is_host_deleted'
	},
	ga4: {
		label: 'профиль',
		arrayKey: 'ga4_view',
		activeKey: 'is_ga4_integration_active',
		tokenKey: 'ga4_integration_token_valid',
		deletedKey: 'is_view_deleted'
	}
}

export default {
	name: 'limitStatistic',
	metaInfo () {
		return {
			title: this.title
		}
	},

	mixins: [updateUrlMixin],

	components: {
		'v-filters': filters
	},

	data () {
		return {
			title: 'Проекты',
			TARIFF_NAMES,
			SORTING_AVAILABLE,
			statusFilterItems: STATUS_FILTER_ITEMS,
			options: {},
			columnFilters: {},
			headers: [
				{
					text: '№',
					align: 'center',
					divider: true,
					value: 'number',
					sortable: false
				},
				{
					text: 'id клиента',
					value: 'client_id',
					align: 'center',
					divider: true
				},
				{
					text: 'Имя клиента',
					value: 'client_name',
					align: 'center',
					divider: true,
					sortable: true
				},
				{
					text: 'Тариф',
					value: 'tariff_name',
					align: 'center',
					divider: true
				},
				{
					text: 'id проекта',
					value: 'project_id',
					align: 'center',
					divider: true
				},
				{
					text: 'Название проекта',
					value: 'project_name',
					align: 'center',
					divider: true,
					sortable: true
				},
				{
					text: 'Главный хост проекта',
					value: 'main_project_host',
					align: 'center',
					divider: true
				},
				{
					text: 'Кол-во активной семантики',
					value: 'current_semantic_count',
					align: 'center',
					divider: true
				},
				{
					text: 'Кол-во сборов. Текущий мес.',
					value: 'previous_month_updates_count',
					align: 'center',
					divider: true
				},
				{
					text: 'Кол-во сборов. Пред. мес.',
					value: 'total_finished_semantic_count',
					align: 'center',
					divider: true
				},
				{
					text: 'Процент от сборов',
					value: 'update_limits_prc',
					align: 'center',
					divider: true
				},
				{
					text: 'YWM',
					align: 'center',
					children: [
						{
							text: 'Активные хосты',
							value: 'ywm_hosts',
							searchKey: 'ywm_host',
							filterType: 'search',
							align: 'center',
							divider: true,
							cellClass: 'ywm-custom-class'
						},
						{
							text: 'Email',
							value: 'ywm_email',
							searchKey: 'ywm_email',
							filterType: 'search',
							align: 'center',
							divider: true
						},
						{
							text: 'Статус',
							value: 'ywm_status',
							searchKey: 'ywm_status',
							filterType: 'select',
							align: 'center',
							divider: true
						}
					]
				},
				{
					text: 'GSC',
					align: 'center',
					children: [
						{
							text: 'Активные хосты',
							value: 'gsc_hosts',
							searchKey: 'gsc_host',
							filterType: 'search',
							align: 'center',
							divider: true,
							cellClass: 'gsc-custom-class'
						},
						{
							text: 'Email',
							value: 'gsc_email',
							searchKey: 'gsc_email',
							filterType: 'search',
							align: 'center',
							divider: true
						},
						{
							text: 'Статус',
							value: 'gsc_status',
							searchKey: 'gsc_status',
							filterType: 'select',
							align: 'center',
							divider: true
						}
					]
				},
				{
					text: ' GA4',
					align: 'center',
					children: [
						{
							text: 'id профиля',
							value: 'ga4_views',
							searchKey: 'ga4_view_id',
							filterType: 'search',
							align: 'center',
							divider: true,
							cellClass: 'ga-custom-class'
						},
						{
							text: 'Email',
							value: 'ga4_email',
							searchKey: 'ga4_email',
							filterType: 'search',
							align: 'center',
							divider: true
						},
						{
							text: 'Статус',
							value: 'ga4_status',
							searchKey: 'ga4_status',
							filterType: 'select',
							align: 'center',
							divider: true
						}

					]
				},
				{
					text: ' YM',
					align: 'center',
					children: [
						{
							text: 'id счетчика',
							value: 'ym_counter',
							searchKey: 'ym_counter',
							filterType: 'search',
							align: 'center',
							divider: true,
							cellClass: 'yw-custom-class'
						},
						{
							text: 'Email',
							value: 'ym_email',
							searchKey: 'ym_email',
							filterType: 'search',
							align: 'center',
							divider: true
						},
						{
							text: 'Статус',
							value: 'ym_status',
							align: 'center',
							searchKey: 'ym_status',
							filterType: 'select',
							divider: true,
							cellClass: 'yw-custom-class'
						}

					]
				},
				{
					text: ' YLA',
					align: 'center',
					children: [
						{
							text: 'id счетчика',
							value: 'yla_counter',
							searchKey: 'yla_counter',
							filterType: 'search',
							align: 'center',
							divider: true,
							cellClass: 'yla-custom-class'
						},
						{
							text: 'Email',
							value: 'yla_email',
							searchKey: 'yla_email',
							filterType: 'search',
							align: 'center',
							divider: true
						},
						{
							text: 'Статус',
							value: 'yla_status',
							align: 'center',
							searchKey: 'yla_status',
							filterType: 'select',
							divider: true,
							cellClass: 'yw-custom-class'
						}

					]
				},
				{
					text: 'Тип яндекс интеграции',
					value: 'active_type',
					align: 'center',
					divider: true
				}
			],
			loading: {
				clients: true,
				table: true
			}
		}
	},

	computed: {
		...mapState({
			clients: state => state.clients,
			projects: state => state.options.projects,
			limitStatisticData: state => state.limitStatistic.limitStatisticData?.items || [],
			limitStatisticTotal: state => state.limitStatistic.limitStatisticData.total || 0
		}),

		hasNestedHeaders () {
			return this.headers.some(header => header.children && header.children.length > 0)
		},

		flatHeaders () {
			return this.headers.map(header => {
				if (header.children && header.children.length > 0) {
					return {
						...header,
						colspan: header.children.length
					}
				}
				return header
			})
		},

		flatHeadersForData () {
			const result = []
			this.headers.forEach(header => {
				if (header.children && header.children.length > 0) {
					header.children.forEach(child => {
						result.push(child)
					})
				} else {
					result.push(header)
				}
			})
			return result
		},

		urlFilters () {
			const {
				client_id,
				project_id,
				tariff_name,
				update_limits_prc,
				main_project_host,
				sort,
				page,
				limit,
				filters,
				client_name,
				project_name,
				ywm_status,
				yla_status,
				ym_status,
				gsc_status,
				ga4_status,
				ywm_host,
				gsc_host,
				ga4_view_id,
				ym_counter,
				yla_counter,
				ywm_email,
				gsc_email,
				ym_email,
				yla_email,
				ga4_email
			} = this.$route.query

			const sortName = sort ? String(sort).replace('-', '') : 'client_id'
			const sortNameValidated = validateFilterFromOptions(sortName, SORTING_AVAILABLE, 'client_id')
			const sortWithDirection = String(sort).startsWith('-') ? `-${sortNameValidated}` : sortNameValidated

			return {
				projectId: validateFilterNumber(+project_id),
				clientId: validateFilterFromOptions(+client_id, this.clients),
				tariffName: validateFilterFromOptions(tariff_name, TARIFF_NAMES, undefined),
				updateLimitsPrc: validateFilterNumber(+update_limits_prc),
				main_project_host: main_project_host,
				client_name: client_name,
				project_name: project_name,
				gsc_host: gsc_host,
				ywm_host: ywm_host,
				ga4_view_id: ga4_view_id,
				ym_counter: ym_counter,
				yla_counter: yla_counter,
				ywm_status: ywm_status,
				yla_status: yla_status,
				ym_status: ym_status,
				gsc_status: gsc_status,
				ga4_status: ga4_status,
				ywm_email: ywm_email,
				gsc_email: gsc_email,
				ga4_email: ga4_email,
				ym_email: ym_email,
				yla_email: yla_email,
				sort: [sortWithDirection],
				page: page || 1,
				limit: limit || 20,
				filters
			}
		},

		tableItems () {
			return this.limitStatisticData.map(item => {
				let activeType = null
				let activeBlock = null

				if (item.integration.ym.is_ym_integration_active === 1) {
					activeType = 'ym'
					activeBlock = item.integration.ym
				} else if (item.integration.yla.is_yla_integration_active === 1) {
					activeType = 'yla'
					activeBlock = item.integration.yla
				}

				return {
					...item,
					BLOCK_CONFIG,
					client_id: item.client.client_id,
					client_name: item.client.client_name,
					tariff_name: item.client.tariff_name,
					project_id: item.project.project_id,
					project_name: item.project.project_name,
					main_project_host: item.project.main_project_host,
					current_semantic_count: item.statistics.current_semantic_count,
					previous_month_updates_count: item.statistics.previous_month_updates_count,
					total_finished_semantic_count: item.statistics.total_finished_semantic_count,
					update_limits_prc: item.statistics.update_limits_prc,
					ywm_hosts: item.integration.ywm?.ywm_hosts
						?.map(h => h.host)
						.join(', ') ?? '-',
					gsc_hosts: item.integration.gsc?.gsc_hosts
						?.map(h => h.host_name)
						.join(', ') ?? '-',
					ga4_views: item.integration.ga4?.ga4_view
						?.map(h => h.view_id)
						.join(', ') ?? '-',
					yla_counter: item.integration.yla?.yla_counter
						?.map(h => h.counter)
						.join(', ') ?? '-',
					ym_counter: item.integration.ym?.ym_counter
						?.map(h => h.counter)
						.join(', ') ?? '-',
					ywm_email: item.integration.ywm.ywm_email ?? '-',
					gsc_email: item.integration.gsc.gsc_email ?? '-',
					ga4_email: item.integration.ga4.ga4_email ?? '-',
					ym_email: item.integration.ym.ym_email ?? '-',
					yla_email: item.integration.yla.yla_email ?? '-',
					active_type: activeType,
					active_block: activeBlock
				}
			})
		}
	},

	async created () {
		try {
			await this.getClients()
		} catch (_) {
			this.$notify({
				type: NOTIFY_STATUS.ERROR,
				title: errors.GET_DATA
			})
		} finally {
			this.loading.clients = false
		}
		await this.initDataTable()
	},

	watch: {
		async '$route.query' () {
			await this.initDataTable()
		},

		options (to) {
			const filters = {}
			if (this.urlFilters.page !== to.page) {
				filters.page = to.page
			}

			if (this.urlFilters.limit !== to.itemsPerPage) {
				filters.limit = to.itemsPerPage
			}

			if (filters.page || filters.limit) {
				this.updateUrl(filters)
			}
		}
	},

	methods: {
		...mapActions({
			getClients: 'getClients',
			getLimitStatisticData: 'limitStatistic/getLimitStatisticData',
			switchYMIntegrationType: 'yla/switchYMIntegrationType'
		}),

		isHeaderSortable (header) {
			return !!header.value && SORTING_AVAILABLE.includes(header.value)
		},

		async initDataTable () {
			const {
				clientId,
				projectId,
				tariffName,
				updateLimitsPrc,
				main_project_host,
				sort,
				client_name,
				project_name,
				ywm_host,
				ywm_email,
				ywm_status,
				yla_status,
				ym_status,
				gsc_status,
				ga4_status,
				gsc_host,
				ym_counter,
				yla_counter,
				gsc_email,
				ga4_view_id,
				ga4_email,
				ym_email,
				yla_email
			} = this.urlFilters

			this.loading.table = true
			await this.getLimitStatisticData({
				query: {
					client_id: clientId != null ? String(clientId) : undefined,
					project_id: projectId != null ? String(projectId) : undefined,
					tariff_name: tariffName || undefined,
					update_limits_prc: updateLimitsPrc,
					main_project_host: main_project_host,
					client_name: client_name,
					project_name: project_name,
					...(gsc_status === 'active' ? { is_gsc_integration_active: '1' } : {}),
					...(gsc_status === 'inactive' ? { is_gsc_integration_active: '0' } : {}),
					...(gsc_status === 'invalid_token' ? { gsc_integration_token_valid: '0' } : {}),
					...(gsc_status === 'invalid_entity' ? { gsc_invalid_host: '1' } : {}),
					...(ywm_status === 'active' ? { is_ywm_integration_active: '1' } : {}),
					...(ywm_status === 'inactive' ? { is_ywm_integration_active: '0' } : {}),
					...(ywm_status === 'invalid_token' ? { ywm_integration_token_valid: '0' } : {}),
					...(ywm_status === 'invalid_entity' ? { ywm_invalid_host: '1' } : {}),
					...(yla_status === 'active' ? { is_yla_integration_active: '1' } : {}),
					...(yla_status === 'inactive' ? { is_yla_integration_active: '0' } : {}),
					...(yla_status === 'invalid_token' ? { yla_integration_token_valid: '0' } : {}),
					...(yla_status === 'invalid_entity' ? { yla_invalid_counter: '1' } : {}),
					...(ym_status === 'active' ? { is_ym_integration_active: '1' } : {}),
					...(ym_status === 'inactive' ? { is_ym_integration_active: '0' } : {}),
					...(ym_status === 'invalid_token' ? { ym_integration_token_valid: '0' } : {}),
					...(ym_status === 'invalid_entity' ? { ym_invalid_counter: '1' } : {}),
					...(ga4_status === 'active' ? { is_ga4_integration_active: '1' } : {}),
					...(ga4_status === 'inactive' ? { is_ga4_integration_active: '0' } : {}),
					...(ga4_status === 'invalid_token' ? { ga4_integration_token_valid: '0' } : {}),
					...(ga4_status === 'invalid_entity' ? { ga4_invalid_property: '1' } : {}),
					gsc_host: gsc_host,
					ywm_host: ywm_host,
					ym_counter: ym_counter,
					yla_counter: yla_counter,
					ga4_view_id: ga4_view_id,
					ywm_email: ywm_email,
					gsc_email: gsc_email,
					ga4_email: ga4_email,
					ym_email: ym_email,
					yla_email: yla_email,
					sort: sort[0],
					offset: (this.urlFilters.page - 1) * this.urlFilters.limit,
					limit: this.urlFilters.limit || 20
				}
			})
			this.loading.table = false
		},

		updateSortBy (name) {
			this.updateUrl({ sort: name })
		},

		updateSortDesc (value) {
			if (value !== undefined) {
				const sortName = this.urlFilters.sort[0].replace('-', '')
				this.updateUrl({ sort: `${value ? '-' : ''}${sortName}` })
			}
		},

		getStatus (block, type) {
			if (!block || !type) {
				return { text: '-', color: '' }
			}

			const config = BLOCK_CONFIG[type]
			if (!config) {
				return { text: '-', color: '' }
			}

			const tokenValid = block[config.tokenKey]
			const intActive = block[config.activeKey]
			const items = block[config.arrayKey] ?? []
			const noChangeItem = {
				yla: { field: 'counter', mode: 'some' },
				ym: { field: 'counter', mode: 'some' },
				ga4: { field: 'view_id', mode: 'some' },
				gsc: { field: 'host', mode: 'every' },
				ywm: { field: 'host', mode: 'every' }
			}
			const rule = noChangeItem[type]

			if (!intActive) {
				return { text: '-', color: '' }
			}

			if (!tokenValid) {
				return { text: 'невалидный токен', color: 'status-red' }
			}

			if (rule) {
				const isInvalid =
					rule.mode === 'some'
						? !items.some(item => item[rule.field] != null)
						: items.length > 0 && items.every(item => item[rule.field] == null)
				if (isInvalid) {
					return {
						text: `настройка не завершена, не выбран ${config.label || ''}`,
						color: 'status-red'
					}
				}
			}

			for (const item of items) {
				if (item[config.deletedKey]) {
					return {
						text: `отозваны права на ${config.label}`,
						color: 'status-red'
					}
				}

				if (config.verifiedKey && !item[config.verifiedKey]) {
					return {
						text: `отозваны права на ${config.label}`,
						color: 'status-red'
					}
				}
			}

			return { text: 'активен', color: 'status-green' }
		},

		getActiveCounters (item) {
			if (!item || !item.active_type || !item.active_block) {
				return []
			}

			const config = BLOCK_CONFIG[item.active_type]
			if (!config || !config.arrayKey) {
				return []
			}

			return item.active_block[config.arrayKey] ?? []
		},

		async switchTypeYMIntegration (item) {
			const integrationIdField = BLOCK_CONFIG[item.active_type].idKey
			await this.switchYMIntegrationType(item.active_block[integrationIdField])
		},

		onFilterChange (key, value) {
			this.$router.replace({
				query: {
					...this.$route.query,
					[key]: value,
					page: 1
				}
			})
		},

		onSort (header) {
			if (!this.isHeaderSortable(header)) {
				return
			}
			const currentSort = this.$route.query.sort
			let nextSort

			if (currentSort === header.value) {
				nextSort = `-${header.value}`
			} else if (currentSort === `-${header.value}`) {
				nextSort = undefined
			} else {
				nextSort = header.value
			}

			this.$router.replace({
				query: {
					...this.$route.query,
					sort: nextSort,
					page: 1
				}
			})
		},

		isSortedBy (header) {
			const sort = this.$route.query.sort
			return sort === header.value || sort === `-${header.value}`
		},

		isSortDesc (header) {
			return this.$route.query.sort === `-${header.value}`
		},

		getItemClass (item) {
			const classes = {
				is_ywm_integration_active: item.integration.ywm.is_ywm_integration_active === 1 ? 'ywm-active-cell' : 'ywm-no-active-cell',
				is_gsc_integration_active: item.integration.gsc.is_gsc_integration_active === 1 ? 'gsc-active-cell' : 'gsc-no-active-cell',
				is_ym_integration_active: item.integration.ym.is_ym_integration_active === 1 ? 'ym-active-cell' : 'ym-no-active-cell',
				is_yla_integration_active: item.integration.yla.is_yla_integration_active === 1 ? 'yla-active-cell' : 'yla-no-active-cell',
				is_ga4_integration_active: item.integration.ga4.is_ga4_integration_active === 1 ? 'ga-active-cell' : 'ga-no-active-cell'
			}
			return Object.values(classes)
		},

		getSelectFilterValue (key) {
			return this.$route.query[key] ?? null
		},

		onSelectFilterChange (key, value) {
			const query = { ...this.$route.query, page: 1 }

			if (value === null) {
				delete query[key]
			} else {
				query[key] = value
			}

			this.$router.replace({ query })
		}
	}
}
</script>

<style >
.limit-statistic-table {
	margin-top: 24px;
	border-top: 1px solid rgba(0,0,0,.12);
}

.custom-th {
	border-right: 1px solid rgba(0, 0, 0, 0.12);
}

.sortable-header-cell {
	cursor: pointer;
}

.search-cell {
	padding: 0 4px !important;
}

.status-cell {
	text-align: center;
}

.status-cell.status-green {
	background-color: #e2ffee;
}
.status-cell.status-red {
	background-color: #fff0ec;
}

.ywm-active-cell .ywm-custom-class,
.gsc-active-cell .gsc-custom-class,
.ym-active-cell .ym-custom-class,
.yla-active-cell .yla-custom-class,
.ga-active-cell .ga-custom-class {
  background-color: #e2ffee;
}

.ywm-no-active-cell .ywm-custom-class,
.gsc-no-active-cell .gsc-custom-class,
.ym-no-active-cell .ym-custom-class,
.yla-no-active-cell .yla-custom-class,
.ga-no-active-cell .ga-custom-class {
  background-color: #fff0ec;
}
</style>
